import React, { useState, useMemo, useCallback, useEffect } from 'react';
import SubTabs from '../../layout/SubTabs';
import DimensionHeader from '../../common/DimensionHeader';
import { useTheme } from '../../../utils/ThemeSwitcher';
import { AnimatedNumber, AnimatedCard } from '../../common/AnimationUtils';
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Search,
  Download,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Grid3X3,
  Table,
  Info,
  ArrowUpDown,
  Clock,
  Timer,
  Activity,
  Calendar,
  Users,
  ClipboardList
} from 'lucide-react';
import DurationHeatmap from '../charts/DurationHeatmap';
import DurationTimelineChart from '../charts/DurationTimelineChart';
import DistributionHistogram from '../charts/DistributionHistogram';
import ScoreCard from '../charts/ScoreCard';
import DetailedIssueTooltip from './DetailedIssueTooltip';  // NEW: Hover tooltip for row details
import IssueLogPanel from './IssueLogPanel';  // Inline issue log tab
import PulseBeacon from './PulseBeacon';  // Animated issue indicator
import CollapsibleSection from './CollapsibleSection';
import EmptyStateCelebration from './EmptyStateCelebration';  // NEW: Time-series trend visualization
import './DurationSection.css';

// Hoisted to module scope: defining this inside the section component gave it a new
// identity on every re-render, which remounted every card and restarted the load animation.
const MetricCard = ({ colors, isDarkMode, onViewIssues, icon: Icon, title, value, subtitle, color, borderColor, detailedIssues = [], issueType = 'duration', totalCount = null, severity, animDelay = 0 }) => {
  const sevColor = severity === 'critical' ? '#b91c1c' : severity === 'warning' ? '#b45309' : null;
  const sevColorDark = severity === 'critical' ? '#f87171' : severity === 'warning' ? '#fbbf24' : null;
  const iconColor = isDarkMode ? (sevColorDark || '#60a5fa') : (sevColor || '#2563EB');
  const hasIssues = detailedIssues.length > 0;

  const cardContent = (
    <div
      className="nsx-metric-card"
      style={{
        position: 'relative',
        padding: '18px',
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
        borderRadius: '8px',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
        overflow: 'hidden',
        cursor: hasIssues ? 'pointer' : 'default',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}
    >
      {severity && (
        <svg
          width="30" height="30" viewBox="0 0 30 30"
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
        >
          <path
            d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
            stroke={iconColor} strokeWidth="2" fill="none"
            strokeLinecap="round" opacity={0.9}
          />
        </svg>
      )}

      <PulseBeacon severity={severity} isDarkMode={isDarkMode} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '5px',
          background: severity
            ? (isDarkMode
                ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
            : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: iconColor,
        }}>
          <Icon size={16} strokeWidth={1.75} />
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            marginBottom: '10px',
          }}>
            {title}
            {hasIssues && (
              <Info size={11} style={{ color: '#2563EB', strokeWidth: 2 }} />
            )}
          </div>
          <div
            className="metric-value"
            style={{
              fontSize: '32px',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color: colors.text,
              lineHeight: 1.05,
              marginBottom: '6px',
            }}
          >
            <AnimatedNumber value={value} duration={800} />
          </div>
          <div style={{
            fontSize: '11.5px',
            fontWeight: 400,
            letterSpacing: '-0.005em',
            color: hasIssues ? '#2563EB' : colors.textMuted,
            lineHeight: 1.4,
          }}>
            {hasIssues ? 'View issue log →' : subtitle}
          </div>
        </div>
        <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
      </div>
    </div>
  );

  if (hasIssues) {
    return (
      <AnimatedCard delay={animDelay} translateY={12}>
      <div onClick={() => onViewIssues && onViewIssues()}>
        {cardContent}
      </div>
      </AnimatedCard>
    );
  }

  return <AnimatedCard delay={animDelay} translateY={12}>{cardContent}</AnimatedCard>;
};

const DurationSection = ({ data, dataCollectorId, isSidePanelExpanded, isMobile = false }) => {
  const { isDarkMode } = useTheme();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('summary');
  
  // Collector Analysis view toggle (heatmap is default)
  const [collectorView, setCollectorView] = useState('heatmap');
  
  // Collector Table state
  const [collectorSortField, setCollectorSortField] = useState('outlierPercentage');
  const [collectorSortDirection, setCollectorSortDirection] = useState('desc');

  // Trend chart mounts after the rows above have finished their load sequence
  // (metrics 0-240ms, analysis row 320-640ms + wave), so it doesn't draw first.
  const [trendReady, setTrendReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTrendReady(true), 1150);
    return () => clearTimeout(t);
  }, []);

  // Color scheme
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt: isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border: isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight: isDarkMode ? '#2A2A2E' : '#F3F4F6',
    text: isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    critical: '#DC2626',
    accent: isDarkMode ? '#5f5f5f' : '#a8a8a8',
  };

  // Early return if no data
  if (!data?.overall) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '32px'
      }}>
        <div className="nsx-metric-card" style={{
          textAlign: 'center',
          padding: '32px',
          borderRadius: '16px',
          backgroundColor: colors.cardBg,
          border: `1px solid ${colors.border}`
        }}>
          <Timer size={48} style={{ color: colors.textMuted, marginBottom: '12px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            No Duration Data Available
          </h3>
          <p style={{ fontSize: '13px', color: colors.textSecondary }}>
            Run a duration analysis to see the results here.
          </p>
        </div>
      </div>
    );
  }

  const overallData = data.overall;
  const hasTimelineData = overallData.timeline_data && overallData.timeline_data.length > 0;
  const hasCollectorData = data.by_collector && dataCollectorId && Object.keys(data.by_collector).length > 0;
  const unit = overallData.unit || 'minutes';
  const durationRange = overallData.duration_range || null;

  // Get duration stats with fallbacks for different field names
  const getDurationValue = (field) => {
    return overallData[field] ?? overallData[`${field}_minutes`] ?? 0;
  };

  // Rating thresholds for duration quality (based on outlier percentage)
  const getStatusInfo = (outlierPercentage) => {
    if (outlierPercentage === 0) return { label: 'Excellent', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.1)' };
    if (outlierPercentage < 5) return { label: 'Good', color: '#34D399', bgColor: 'rgba(52, 211, 153, 0.1)' };
    if (outlierPercentage < 10) return { label: 'Moderate', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.1)' };
    if (outlierPercentage < 20) return { label: 'Poor', color: '#F97316', bgColor: 'rgba(249, 115, 22, 0.1)' };
    return { label: 'Critical', color: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.1)' };
  };

  // Calculate overall statistics
  const overallStats = useMemo(() => {
    const totalObservations = overallData.total_observations || 0;
    const validObservations = overallData.valid_observations || 0;
    const meanDuration = getDurationValue('mean_duration');
    const medianDuration = getDurationValue('median_duration');
    const minDuration = getDurationValue('min_duration');
    const maxDuration = getDurationValue('max_duration');
    const stdDuration = getDurationValue('std_duration');
    const outlierCount = overallData.outlier_count || 0;
    const outlierPercentage = overallData.outlier_percentage || 0;
    
    // Quality Score = 100 - outlier percentage
    const qualityScore = 100 - outlierPercentage;
    
    // Distribution breakdown
    const distribution = overallData.distribution || {};
    
    return {
      totalObservations,
      validObservations,
      meanDuration,
      medianDuration,
      minDuration,
      maxDuration,
      stdDuration,
      outlierCount,
      outlierPercentage,
      qualityScore,
      distribution
    };
  }, [overallData]);

  // NEW: Extract detailed issues from duration data for tooltip
  const detailedDurationIssues = useMemo(() => {
    if (overallData.detailed_issues?.length > 0) {
      return overallData.detailed_issues;
    }
    // Fallback: create from outlier_indices if available
    if (overallData.outlier_indices?.length > 0) {
      return overallData.outlier_indices.map((rowIdx, i) => ({
        row_index: rowIdx,
        variable: 'duration',
        value: overallData.outlier_values?.[i],
        duration_type: null,
        respondent_id: null,
        collector_id: null,
        interview_date: null
      }));
    }
    return [];
  }, [overallData]);

  // Prepare distribution data for pie chart
  // Per-interview durations for the distribution histogram (frontend-only)
  const durationValues = useMemo(() => {
    let vals = [];
    if (overallData.timeline_data && overallData.timeline_data.length > 0) {
      vals = overallData.timeline_data.map(d => d.duration);
    } else if (overallData.raw_data && overallData.raw_data.length > 0 && overallData.variable_analyzed) {
      vals = overallData.raw_data.map(r => r[overallData.variable_analyzed]);
    }
    return vals
      .map(v => (typeof v === 'string' ? parseFloat(v) : v))
      .filter(v => typeof v === 'number' && isFinite(v));
  }, [overallData]);

  const durationInsight = useMemo(() => {
    const n = durationValues.length;
    if (!n) return null;
    const min = durationRange && typeof durationRange.min === 'number' ? durationRange.min : null;
    const max = durationRange && typeof durationRange.max === 'number' ? durationRange.max : null;
    const below = min !== null ? durationValues.filter(v => v < min).length : 0;
    const above = max !== null ? durationValues.filter(v => v > max).length : 0;
    const pBelow = (below / n) * 100;
    const pAbove = (above / n) * 100;
    const pOut = pBelow + pAbove;
    const u = unit === 'minutes' ? 'min' : unit;
    const med = overallStats.medianDuration;
    const medTxt = `Median is ${med.toFixed(1)}${u ? ` ${u}` : ''}.`;
    const tone = pOut >= 20 ? 'critical' : pOut >= 5 ? 'warning' : 'success';

    if (pOut === 0) {
      return { tone: 'success', headline: 'Every interview falls inside the acceptable range.', detail: medTxt };
    }
    if (pBelow >= pAbove) {
      const floor = `${min}${u ? `-${u.replace('min', 'minute')}` : ''}`;
      return {
        tone,
        headline: `${pBelow.toFixed(1)}% of interviews finish below the ${floor} floor.`,
        detail: `${medTxt} ${pBelow >= 25
          ? 'The pattern points to rushed administration, not a few stragglers.'
          : pBelow >= 5
            ? 'Worth checking which collectors drive the short interviews.'
            : 'A small tail of short interviews; spot-check rather than escalate.'}${pAbove > 0 ? ` ${pAbove.toFixed(1)}% run past the ceiling.` : ''}`
      };
    }
    const ceiling = `${max}${u ? `-${u.replace('min', 'minute')}` : ''}`;
    return {
      tone,
      headline: `${pAbove.toFixed(1)}% of interviews run past the ${ceiling} ceiling.`,
      detail: `${medTxt} Long interviews usually mean interruptions or forms left open; review the collectors involved.${pBelow > 0 ? ` ${pBelow.toFixed(1)}% finish below the floor.` : ''}`
    };
  }, [durationValues, durationRange, unit, overallStats.medianDuration]);

  // Prepare collector data for table
  const collectorTableData = useMemo(() => {
    if (!data.by_collector) return [];
    
    return Object.entries(data.by_collector).map(([collector, collectorData]) => {
      const meanDuration = collectorData.mean_duration ?? collectorData.mean_duration_minutes ?? 0;
      const medianDuration = collectorData.median_duration ?? collectorData.median_duration_minutes ?? 0;
      const minDuration = collectorData.min_duration ?? collectorData.min_duration_minutes ?? 0;
      const maxDuration = collectorData.max_duration ?? collectorData.max_duration_minutes ?? 0;
      const stdDuration = collectorData.std_duration ?? collectorData.std_duration_minutes ?? 0;
      const validObservations = collectorData.valid_observations || 0;
      const totalObservations = collectorData.total_observations || 0;
      const outlierCount = collectorData.outlier_count || 0;
      const outlierPercentage = collectorData.outlier_percentage || 0;
      const status = getStatusInfo(outlierPercentage);

      return {
        collector,
        meanDuration,
        medianDuration,
        minDuration,
        maxDuration,
        stdDuration,
        validObservations,
        totalObservations,
        outlierCount,
        outlierPercentage,
        qualityScore: 100 - outlierPercentage,
        status: status.label,
        statusColor: status.color
      };
    });
  }, [data.by_collector]);

  // Sort collector data
  const sortedCollectorData = useMemo(() => {
    const sorted = [...collectorTableData];
    sorted.sort((a, b) => {
      let aVal = a[collectorSortField];
      let bVal = b[collectorSortField];
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (collectorSortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    return sorted;
  }, [collectorTableData, collectorSortField, collectorSortDirection]);

  // Export to CSV
  const exportToCSV = useCallback(() => {
    let csv = 'Collector,Mean Duration,Median Duration,Min,Max,Std Dev,Valid Obs,Total Obs,Outliers,Outlier %,Quality Score,Status\n';
    sortedCollectorData.forEach(c => {
      csv += `${c.collector},${c.meanDuration.toFixed(1)},${c.medianDuration.toFixed(1)},${c.minDuration.toFixed(1)},${c.maxDuration.toFixed(1)},${c.stdDuration.toFixed(1)},${c.validObservations},${c.totalObservations},${c.outlierCount},${c.outlierPercentage.toFixed(1)}%,${c.qualityScore.toFixed(1)}%,${c.status}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'duration_collector_analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  }, [sortedCollectorData]);

  // Toggle sort
  const toggleSort = (field) => {
    if (collectorSortField === field) {
      setCollectorSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setCollectorSortField(field);
      setCollectorSortDirection('desc');
    }
  };

  // Sortable header component
  const SortableHeader = ({ field, label, align = 'left' }) => {
    const isActive = collectorSortField === field;
    
    return (
      <th 
        onClick={() => toggleSort(field)}
        style={{ 
          padding: '14px 16px', 
          textAlign: align, 
          fontSize: '13px', 
          fontWeight: '600',
          color: isActive ? colors.accent : colors.textSecondary,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          borderBottom: `2px solid ${colors.border}`
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
          {label}
          <ArrowUpDown size={14} style={{ opacity: isActive ? 1 : 0.4 }} />
          {isActive && (
            collectorSortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
          )}
        </div>
      </th>
    );
  };

  // Metric card component
  // MetricCard component with optional tooltip for detailed issues

  // Get quality level
  const qualityLevel = getStatusInfo(overallStats.outlierPercentage);

  // Tab definitions — timeline chart is promoted to Summary, so no separate tab
  const tabs = [
    { id: 'summary', label: 'Summary Dashboard', icon: BarChart3 },
    ...(hasCollectorData ? [{ id: 'collectors', label: 'Collector Analysis', icon: Users }] : []),
    ...(detailedDurationIssues.length > 0 ? [{ id: 'issues', label: 'Issue Log', icon: ClipboardList }] : [])
  ];

  // Tab Navigation Component
  // ============== RENDER SECTIONS ==============

  // Summary Dashboard Tab
  const renderSummaryDashboard = () => (
    <div style={{ 
      display: 'grid', 
      gap: '16px',
      marginTop: '80px'
    }}>
      {/* Analysis Configuration */}
      <div className="nsx-metric-card" style={{
        padding: '12px 16px',
        backgroundColor: colors.cardBgAlt,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        fontSize: '14px',
        color: colors.textSecondary
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <Info size={16} />
          <span style={{ fontWeight: '600', color: colors.text }}>Analysis Configuration</span>
        </div>
        <div>
          {overallData.duration_type === 'pre_calculated' && (
            <span>Pre-calculated duration variable: <strong style={{ color: colors.text }}>{overallData.variable_analyzed}</strong></span>
          )}
          {overallData.duration_type === 'calculated_from_timestamps' && (
            <span>Duration calculated from <strong style={{ color: colors.text }}>{overallData.start_variable}</strong> to <strong style={{ color: colors.text }}>{overallData.end_variable}</strong></span>
          )}
          {overallData.duration_type === 'duration_with_date' && (
            <span>Duration timeline: <strong style={{ color: colors.text }}>{overallData.duration_variable}</strong> over <strong style={{ color: colors.text }}>{overallData.date_variable}</strong></span>
          )}
          {durationRange && (
            <span style={{ marginLeft: '16px', padding: '4px 12px', backgroundColor: colors.cardBg, borderRadius: '6px' }}>
              Acceptable range: <strong>{durationRange.min} - {durationRange.max} {unit}</strong>
            </span>
          )}
        </div>
      </div>

      <CollapsibleSection 
        title="Key Metrics" 
        isDarkMode={isDarkMode} 
        colors={colors}
        badge={
          overallStats.outlierPercentage >= 10 ? (
            <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#EF444420', color: '#EF4444', fontSize: '11px', fontWeight: '600' }}>
              {overallStats.outlierCount.toLocaleString()} outliers
            </span>
          ) : null
        }
      >
      {/* Top Metrics Row */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '16px' 
      }}>
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={0}
          icon={BarChart3}
          title="Total Observations"
          value={overallStats.totalObservations.toLocaleString()}
          subtitle={`${overallStats.validObservations.toLocaleString()} valid`}
          color="#6B7280"
          borderColor="#6B7280"
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={80}
          icon={Clock}
          title="Mean Duration"
          value={`${overallStats.meanDuration.toFixed(1)}`}
          subtitle={unit}
          color={colors.accent}
          borderColor={colors.accent}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={160}
          icon={Timer}
          title="Median Duration"
          value={`${overallStats.medianDuration.toFixed(1)}`}
          subtitle={unit}
          color="#6B7280"
          borderColor="#6B7280"
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={240}
          icon={AlertTriangle}
          title="Outliers"
          value={overallStats.outlierCount.toLocaleString()}
          subtitle={`${overallStats.outlierPercentage.toFixed(1)}% of observations`}
          color={overallStats.outlierCount > 0 ? colors.error : colors.success}
          borderColor={overallStats.outlierCount > 0 ? colors.error : colors.success}
          detailedIssues={detailedDurationIssues}
          issueType="duration"
          totalCount={overallStats.outlierCount}
          severity={overallStats.outlierPercentage >= 10 ? 'critical' : overallStats.outlierPercentage >= 3 ? 'warning' : null}
        />
      </div>
      </CollapsibleSection>

      <CollapsibleSection title="Analysis & Issues" isDarkMode={isDarkMode} colors={colors}>
      {/* Compact Analysis Row */}
      <div className="analysis-grid-4col duration-analysis-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1.2fr)', 
        gap: '16px' 
      }}>
        {/* Quality Score */}
        <AnimatedCard delay={320} translateY={12}>
          <ScoreCard
            eyebrow="duration quality"
            score={overallStats.qualityScore}
            label={qualityLevel.label}
            caption={overallStats.qualityScore >= 95 ? 'Excellent duration quality' : overallStats.qualityScore >= 90 ? 'Some interviews outside range' : overallStats.qualityScore >= 80 ? 'Review patterns' : 'Investigation needed'}
            isDarkMode={isDarkMode}
            startDelay={620}
          />
        </AnimatedCard>

        {/* Duration Distribution histogram (range-aligned bins, green in range, red outside) */}
        {durationValues.length > 0 && (
          <div className="duration-hist-cell" style={{ minWidth: 0 }}>
            <AnimatedCard delay={400} translateY={12}>
              <DistributionHistogram
                title="Duration distribution"
                values={durationValues}
                range={durationRange}
                unit={unit === 'minutes' ? 'min' : unit}
                outsideLabel="outside range"
                insight={durationInsight}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
                startDelay={700}
              />
            </AnimatedCard>
          </div>
        )}

        {/* Duration Statistics */}
        <AnimatedCard delay={480} translateY={12}>
        <div className="nsx-metric-card" style={{
          position: 'relative',
          padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden',
          fontFamily: "'Outfit', -apple-system, sans-serif",
        }}>
          <div style={{
            fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em',
            textTransform: 'lowercase', color: colors.textMuted, marginBottom: '14px',
          }}>
            duration statistics
          </div>
          <div>
            {[
              { label: 'Mean', value: `${overallStats.meanDuration.toFixed(1)} ${unit}` },
              { label: 'Median', value: `${overallStats.medianDuration.toFixed(1)} ${unit}` },
              { label: 'Min', value: `${overallStats.minDuration.toFixed(1)} ${unit}` },
              { label: 'Max', value: `${overallStats.maxDuration.toFixed(1)} ${unit}` },
              { label: 'Std Dev', value: `${overallStats.stdDuration.toFixed(1)} ${unit}` }
            ].map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '7px 0',
                borderBottom: idx < 4 ? `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` : 'none',
              }}>
                <span style={{ color: colors.text, fontSize: '12px', letterSpacing: '-0.005em' }}>{item.label}</span>
                <span style={{
                  fontSize: '12.5px', fontWeight: 500, color: colors.text,
                  fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em',
                }}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
        </AnimatedCard>

        {/* Top Collector Issues */}
        <AnimatedCard delay={560} translateY={12}>
        {collectorTableData.length > 0 ? (
          <div className="nsx-metric-card" style={{
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
            borderRadius: '8px',
            border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            overflow: 'hidden',
            padding: '18px',
            fontFamily: "'Outfit', -apple-system, sans-serif",
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              marginBottom: '14px', gap: '10px',
            }}>
              <div>
                <div style={{
                  fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em',
                  textTransform: 'lowercase', color: colors.textMuted, marginBottom: '3px',
                }}>
                  top issues
                </div>
                <div style={{
                  fontSize: '13.5px', fontWeight: 500, letterSpacing: '-0.01em',
                  color: colors.text,
                }}>
                  Top Collector Issues
                </div>
              </div>
              <span style={{
                fontSize: '9.5px', fontWeight: 500, letterSpacing: '0.06em',
                textTransform: 'lowercase', color: colors.textMuted, paddingTop: '14px',
              }}>
                top 5
              </span>
            </div>
            <div style={{ overflowX: 'auto', margin: '0 -4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                <thead>
                  <tr>
                    <th style={{
                      padding: '6px 10px 8px', textAlign: 'left',
                      color: colors.textMuted, fontWeight: 500, fontSize: '9.5px',
                      letterSpacing: '0.06em', textTransform: 'lowercase',
                      borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                    }}>collector</th>
                    <th style={{
                      padding: '6px 10px 8px', textAlign: 'right',
                      color: colors.textMuted, fontWeight: 500, fontSize: '9.5px',
                      letterSpacing: '0.06em', textTransform: 'lowercase',
                      borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                    }}>rate</th>
                    <th style={{
                      padding: '6px 10px 8px', textAlign: 'right',
                      color: colors.textMuted, fontWeight: 500, fontSize: '9.5px',
                      letterSpacing: '0.06em', textTransform: 'lowercase',
                      borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                    }}>status</th>
                  </tr>
                </thead>
                <tbody>
                  {[...collectorTableData].sort((a, b) => b.outlierPercentage - a.outlierPercentage).filter(c => c.outlierPercentage > 0).slice(0, 5).map(c => {
                    const rate = c.outlierPercentage;
                    const toneC = rate >= 20 ? (isDarkMode ? '#f87171' : '#b91c1c')
                              : rate >= 5 ? (isDarkMode ? '#fbbf24' : '#b45309')
                              : (isDarkMode ? '#34d399' : '#047857');
                    const status = getStatusInfo(rate);
                    return (
                      <tr key={c.collector}>
                        <td style={{
                          padding: '9px 10px', color: colors.text, fontWeight: 400,
                          letterSpacing: '-0.005em', whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px',
                        }}>{c.collector}</td>
                        <td style={{
                          padding: '9px 10px', textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          <span style={{ color: toneC, fontWeight: 500, fontSize: '13px' }}>
                            {rate.toFixed(1)}%
                          </span>
                        </td>
                        <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center',
                            padding: '2px 9px', borderRadius: '100px',
                            fontSize: '10px', fontWeight: 500,
                            letterSpacing: '0.04em', textTransform: 'lowercase',
                            background: isDarkMode ? `${toneC}22` : `${toneC}14`,
                            color: toneC, border: `0.5px solid ${toneC}33`,
                          }}>
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {collectorTableData.filter(c => c.outlierPercentage > 0).length === 0 && (
                    <tr><td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: colors.textMuted, fontSize: '12px' }}>No outliers detected</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="nsx-metric-card" style={{ backgroundColor: colors.cardBg, borderRadius: '12px', border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px', color: colors.textMuted, fontSize: '13px' }}>
            No collector data
          </div>
        )}
        </AnimatedCard>
      </div>

      <style>{`
        @media (max-width: 1200px) {
          .duration-analysis-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .duration-analysis-grid .duration-hist-cell { grid-column: 1 / -1; }
        }
        @media (max-width: 768px) { .duration-analysis-grid { grid-template-columns: minmax(0, 1fr) !important; } }
      `}</style>
      </CollapsibleSection>

      {/* Suspicious Entries (if any) */}
      {overallData.suspicious_entries && overallData.suspicious_entries.length > 0 && (
        <div style={{
          padding: '18px',
          backgroundColor: isDarkMode ? '#1F1116' : '#FEF2F2',
          borderRadius: '12px',
          border: `1px solid ${colors.error}40`
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <XCircle size={20} style={{ color: colors.error }} />
            <span style={{ fontSize: '14px', fontWeight: '600', color: colors.error }}>
              Suspicious Entries (Negative Duration)
            </span>
            <span style={{ 
              padding: '2px 8px', 
              backgroundColor: colors.error, 
              color: 'white', 
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {overallData.suspicious_entries.length}
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  {Object.keys(overallData.suspicious_entries[0] || {}).map(key => (
                    <th key={key} style={{ 
                      padding: '10px', 
                      textAlign: 'left', 
                      borderBottom: `1px solid ${colors.error}40`,
                      color: colors.text
                    }}>
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {overallData.suspicious_entries.slice(0, 10).map((entry, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                    {Object.values(entry).map((value, vidx) => (
                      <td key={vidx} style={{ padding: '10px', color: colors.text }}>
                        {value instanceof Date ? value.toLocaleString() : String(value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {overallData.suspicious_entries.length > 10 && (
              <div style={{ padding: '12px', textAlign: 'center', color: colors.textMuted, fontSize: '12px' }}>
                Showing 10 of {overallData.suspicious_entries.length} suspicious entries
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trend — collapsible, hover-lift card */}
      {hasTimelineData && (
        <CollapsibleSection title="Trend" isDarkMode={isDarkMode} colors={colors}>
          <AnimatedCard delay={880} translateY={12}>
            <div className="nsx-metric-card" style={{
              padding: '18px',
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
              borderRadius: '8px',
              border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              fontFamily: "'Outfit', -apple-system, sans-serif",
            }}>
              {trendReady ? (
                <DurationTimelineChart
                  data={overallData}
                  byCollectorData={data.by_collector}
                  height={isMobile ? 420 : 460}
                  isDarkMode={isDarkMode}
                />
              ) : (
                // Same footprint as the chart so nothing below jumps when it mounts
                <div style={{ height: isMobile ? 420 : 460 }} aria-hidden="true" />
              )}
            </div>
          </AnimatedCard>
        </CollapsibleSection>
      )}
    </div>
  );

  // Timeline Analysis — promoted into Summary Dashboard, function removed.

  // Collector Analysis Tab
  const renderCollectorAnalysis = () => (
    <div style={{ marginTop: '80px' }}>
      {/* View Toggle */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '14px',
        paddingBottom: '10px',
        borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            marginBottom: '3px',
          }}>
            collector analysis
          </div>
          <h3 style={{
            fontSize: '15px',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            color: colors.text,
            margin: 0,
          }}>
            Duration patterns by data collector
          </h3>
        </div>

        <div style={{ display: 'flex', gap: '0', alignItems: 'center' }}>
          {[
            { key: 'heatmap', icon: Grid3X3, label: 'heatmap' },
            { key: 'table',   icon: Table,   label: 'table' },
          ].map(({ key, icon: VIcon, label }) => {
            const isActive = collectorView === key;
            return (
              <button
                key={key}
                onClick={() => setCollectorView(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '0',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: isActive ? colors.text : colors.textMuted,
                  fontSize: '12px',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  textTransform: 'lowercase',
                  cursor: 'pointer',
                  opacity: isActive ? 1 : 0.7,
                  borderBottom: isActive ? '2px solid #D97706' : '2px solid transparent',
                  marginBottom: '-11px',
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                }}
              >
                <VIcon size={13} strokeWidth={isActive ? 2 : 1.75} />
                {label}
              </button>
            );
          })}
        </div>
      </div>
      </AnimatedCard>

      {/* Export row — shown only for table view, below the toggle */}
      {collectorView === 'table' && (
        <AnimatedCard delay={40} translateY={8}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <button
            onClick={exportToCSV}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              padding: '7px 14px',
              borderRadius: '100px',
              border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
              color: colors.text,
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              cursor: 'pointer',
              backdropFilter: 'blur(12px)',
              fontFamily: "'Outfit', -apple-system, sans-serif",
              transition: 'all 0.2s ease',
            }}
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>
        </AnimatedCard>
      )}

      {/* Heatmap View */}
      <AnimatedCard delay={80} translateY={12}>
      {collectorView === 'heatmap' && (
        <div className="nsx-metric-card" style={{
          padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflowX: 'auto',
        }}>
          <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '6px', lineHeight: 1.45 }}>
            Median interview length per collector per day. Green cells sit inside the acceptable range
            {durationRange ? ` (${durationRange.min}\u2013${durationRange.max} ${unit === 'minutes' ? 'min' : unit})` : ''}; yellow to red shows how far outside it a day's median falls.
          </div>
          <div style={{ minWidth: 'auto', minHeight: 'auto' }}>
            <DurationHeatmap 
              data={data}
              dataCollectorId={dataCollectorId}
              durationRange={durationRange}
              width={isMobile ? 600 : 1000}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      )}
      </AnimatedCard>

      {/* Table View */}
      <AnimatedCard delay={160} translateY={12}>
      {collectorView === 'table' && (
        <div className="nsx-metric-card" style={{
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: colors.cardBgAlt }}>
                  <SortableHeader field="collector" label="Data Collector" />
                  <SortableHeader field="meanDuration" label={`Mean (${unit})`} align="center" />
                  <SortableHeader field="medianDuration" label={`Median (${unit})`} align="center" />
                  <SortableHeader field="minDuration" label="Min" align="center" />
                  <SortableHeader field="maxDuration" label="Max" align="center" />
                  <SortableHeader field="validObservations" label="Valid Obs." align="center" />
                  <SortableHeader field="outlierCount" label="Outliers" align="center" />
                  <SortableHeader field="outlierPercentage" label="Outlier %" align="center" />
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: colors.textSecondary, borderBottom: `2px solid ${colors.border}` }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCollectorData.map((collector) => (
                  <tr key={collector.collector} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '500', color: colors.text }}>
                      {collector.collector}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', color: colors.text }}>
                      {collector.meanDuration.toFixed(1)}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', color: colors.text }}>
                      {collector.medianDuration.toFixed(1)}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', color: colors.textSecondary }}>
                      {collector.minDuration.toFixed(1)}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', color: colors.textSecondary }}>
                      {collector.maxDuration.toFixed(1)}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', color: colors.text }}>
                      {collector.validObservations}/{collector.totalObservations}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: collector.outlierCount > 0 ? colors.error : colors.success }}>
                      {collector.outlierCount}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: collector.statusColor }}>
                      {collector.outlierPercentage.toFixed(1)}%
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '5px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: `${collector.statusColor}15`,
                        color: collector.statusColor
                      }}>
                        {collector.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {sortedCollectorData.length === 0 && (
            <div style={{ padding: '32px', textAlign: 'center', color: colors.textMuted }}>
              No collector data available
            </div>
          )}
        </div>
      )}
      </AnimatedCard>

      {/* Rating Thresholds Legend — only for table view (heatmap has its own built-in legend) */}
      {collectorView === 'table' && (
      <AnimatedCard delay={320} translateY={12}>
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
        borderRadius: '8px',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        gap: '18px',
        flexWrap: 'wrap',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <Info size={13} style={{ color: colors.textMuted, opacity: 0.7 }} />
          <span style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
          }}>
            rating thresholds · by outlier %
          </span>
        </div>
        {[
          { label: 'Excellent', range: '0%',   color: isDarkMode ? '#34d399' : '#047857' },
          { label: 'Good',      range: '<5%',  color: isDarkMode ? '#10b981' : '#059669' },
          { label: 'Moderate',  range: '<10%', color: isDarkMode ? '#fbbf24' : '#b45309' },
          { label: 'Poor',      range: '<20%', color: '#D97706' },
          { label: 'Critical',  range: '≥20%', color: isDarkMode ? '#f87171' : '#b91c1c' },
        ].map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '3px',
              backgroundColor: item.color,
              boxShadow: `0 0 6px ${item.color}33`,
            }} />
            <span style={{
              fontSize: '11.5px',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              color: colors.text,
            }}>
              {item.label}
            </span>
            <span style={{
              fontSize: '10.5px',
              fontWeight: 400,
              color: colors.textMuted,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {item.range}
            </span>
          </div>
        ))}
      </div>
      </AnimatedCard>
      )}
    </div>
  );

  // ============== MAIN RENDER ==============
  return (
    <div 
      className="duration-section activeQualityDetector"
      style={{
        width: '100%',
        maxWidth: '100%',
        padding: isMobile ? '0 8px 24px 8px' : '0 45px 24px 45px',
        boxSizing: 'border-box',
        overflow: isMobile ? 'visible' : 'hidden',
      }}
    >
      <DimensionHeader
        slug="duration"
        title="Duration"
        description="Surfaces surveys completed suspiciously fast or unusually slow. Abnormal durations flag rushed enumerators, technical interruptions, or respondents who disengaged mid-interview."
        rememberCollapseKey="duration"
        stat={{
          score: overallStats.qualityScore,
          scoreLabel: 'quality score',
          unit: '%',
        }}
        tabs={tabs.map(t => {
          if (t.id === 'collectors') return { ...t, count: Object.keys(data?.by_collector || {}).length, countTone: 'neutral' };
          if (t.id === 'issues')     return { ...t, count: detailedDurationIssues.length,
                                              countTone: detailedDurationIssues.length > 0 ? 'alert' : 'neutral' };
          return t;
        })}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMobile={isMobile}
        isSidePanelExpanded={isSidePanelExpanded}
      />
      <div style={{ 
          minHeight: '600px',
          marginTop: '16px'
        }}>
        {activeTab === 'summary' && renderSummaryDashboard()}
        {activeTab === 'collectors' && hasCollectorData && renderCollectorAnalysis()}

        {activeTab === 'issues' && (
          <AnimatedCard delay={0} translateY={12}>
          <IssueLogPanel
            issues={detailedDurationIssues}
            title={`${overallStats.totalOutliers?.toLocaleString() || detailedDurationIssues.length} Duration Outliers`}
            issueType="duration"
            totalCount={overallStats.totalOutliers || detailedDurationIssues.length}
          />
          </AnimatedCard>
        )}
      </div>
    </div>
  );
};

export default DurationSection;